// ---------------------------------------------------------------------------
// Service Wave utility
// ---------------------------------------------------------------------------
// A "service wave" groups kitchen items by the dining flow of each person at
// the table, rather than by a fixed course number.
//
// Wave N means: "fire all items that are the Nth dish for their respective
// seat at the same time."
//
// When seat information is absent the system falls back to courseNumber-based
// waves so that legacy behaviour is fully preserved.
// ---------------------------------------------------------------------------

/** Minimum shape required to compute wave assignments. */
export interface WaveInput {
  courseNumber: number;
  seatNumber?: number | null;
}

// ---------------------------------------------------------------------------
// Core algorithm
// ---------------------------------------------------------------------------

/**
 * Compute service-wave numbers for an array of order items.
 *
 * The returned array has the same length as `items`; each element is the
 * 1-based wave number assigned to the item at that index.
 *
 * Algorithm (two cases):
 *
 * 1. ALL items carry a seatNumber (pure seat-based orchestration)
 *    - Items are grouped by seatNumber.
 *    - Within each seat group the items are sorted by courseNumber ascending.
 *    - The position of an item within its seat group (0-based + 1) becomes
 *      its wave number.
 *    - Items from different seats that share the same wave position are fired
 *      together.
 *
 * 2. ANY item lacks a seatNumber (default / legacy / mixed)
 *    - wave = courseNumber for every item.
 *    - This keeps the kitchen firing antipasti (course 1) first, primi
 *      (course 2) next, etc. regardless of whether the waiter tagged a
 *      partial subset of items with seats.
 */
export function computeServiceWaves(items: WaveInput[]): number[] {
  if (items.length === 0) return [];

  // Default / mixed case: wave equals courseNumber directly.
  //
  // The seat-based logic below only makes sense when EVERY item carries a
  // seat number (true seat-driven orchestration). If even one item lacks a
  // seat, lumping it into an implicit "seat 0" produces wave numbers that
  // contradict the courseNumber the user chose — e.g. a contorno tagged as
  // courseNumber=3 would be fired as wave 1 just because it appeared first
  // in the un-seated bucket. Falling back to wave = courseNumber keeps the
  // kitchen firing antipasti first, primi next, etc. in the common case.
  if (!items.every((i) => i.seatNumber != null)) {
    return items.map((i) => i.courseNumber);
  }

  // Pure seat-based mode: every item has a seatNumber.
  const seatGroups = new Map<number, number[]>();

  for (let idx = 0; idx < items.length; idx++) {
    const seat = items[idx].seatNumber as number;
    if (!seatGroups.has(seat)) seatGroups.set(seat, []);
    seatGroups.get(seat)!.push(idx);
  }

  for (const indices of seatGroups.values()) {
    indices.sort((a, b) => items[a].courseNumber - items[b].courseNumber);
  }

  const waves = new Array<number>(items.length).fill(0);

  for (const indices of seatGroups.values()) {
    for (let pos = 0; pos < indices.length; pos++) {
      waves[indices[pos]] = pos + 1;
    }
  }

  return waves;
}

// ---------------------------------------------------------------------------
// Grouping & query helpers
// ---------------------------------------------------------------------------

/**
 * Group an array of items (each carrying a `serviceWave` field) into a Map
 * keyed by wave number.
 *
 * The Map is ordered by wave number ascending because insertion order is
 * preserved and waves are inserted in sorted order.
 */
export function groupByWave<T extends { serviceWave: number }>(
  items: T[],
): Map<number, T[]> {
  const groups = new Map<number, T[]>();

  // Collect all distinct wave numbers and sort them so the Map iteration
  // order matches the natural wave sequence.
  const waveNumbers = [...new Set(items.map((i) => i.serviceWave))].sort(
    (a, b) => a - b,
  );

  for (const wave of waveNumbers) {
    groups.set(wave, []);
  }

  for (const item of items) {
    groups.get(item.serviceWave)!.push(item);
  }

  return groups;
}

/**
 * Return true when every non-cancelled item that belongs to `waveNumber` has
 * reached READY or SERVED status.
 *
 * An empty wave (or a wave where all items are CANCELLED) is considered
 * complete.
 */
export function isWaveComplete(
  items: Array<{ serviceWave: number; status: string }>,
  waveNumber: number,
): boolean {
  const active = items.filter(
    (i) => i.serviceWave === waveNumber && i.status !== "CANCELLED",
  );
  if (active.length === 0) return true;
  return active.every((i) => i.status === "READY" || i.status === "SERVED");
}

/**
 * Return the highest `serviceWave` value present in the item array.
 * Returns 0 when the array is empty.
 */
export function getMaxWave(items: Array<{ serviceWave: number }>): number {
  return items.reduce((max, i) => Math.max(max, i.serviceWave), 0);
}

/**
 * Return the Italian display label for a wave number.
 * e.g. waveLabel(1) → "Ondata 1", waveLabel(3) → "Ondata 3"
 *
 * Feature 110: termine canonico "Ondata" su tutta la piattaforma
 * (selettore mobile, riepilogo cameriere, KDS).
 */
export function waveLabel(waveNumber: number): string {
  return `Ondata ${waveNumber}`;
}

// ---------------------------------------------------------------------------
// Wave selector options (feature 110 — waiter wave UX)
// ---------------------------------------------------------------------------

/**
 * Bersaglio di un chip della barra ondate: un numero d'ondata oppure il
 * gruppo bevande (che è fuori dalle ondate per definizione).
 */
export type WaveChipTarget = 1 | 2 | 3 | 4 | "drinks";

/**
 * Opzioni del selettore ondata nel modal piatto e dei chip della barra.
 * Sostituiscono COURSE_OPTIONS (i nomi di portata NON vanno mai usati come
 * etichette d'ondata — feature 110).
 */
export const WAVE_OPTIONS: { value: 1 | 2 | 3 | 4; label: string; short: string }[] = [
  { value: 1, label: "Ondata 1", short: "1ª" },
  { value: 2, label: "Ondata 2", short: "2ª" },
  { value: 3, label: "Ondata 3", short: "3ª" },
  { value: 4, label: "Ondata 4", short: "4ª" },
];
